import React, { useState, useEffect } from 'react'
import { Heading, HStack, Select, Stack, Button, Spinner } from '@chakra-ui/react'
import { Helmet } from 'react-helmet'
import { ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/20/solid'
import { ColumnApi, ColumnState, GridApi, GridReadyEvent } from 'ag-grid-community'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { capabilities } from '@homevest/utils'

import Grid from './Grid'
import {
  collectionsMonths as months,
  collectionsYears as years,
  payOrQuitPostingHref,
  payOrQuitPostingSelection
} from './payOrQuitPostingNavigation'
import {
  useCollectionsByMonthQuery,
  useLiabilitiesRemainingForCollectionsQuery
} from 'graphql/generated'
import { hasCapability } from 'lib/admin-perms'
import { StoreState } from 'store'

const Collections = () => {
  const location = useLocation()
  const period = payOrQuitPostingSelection(location.search)
  const [month, setMonth] = useState(period.month)
  const [year, setYear] = useState(period.year)
  const [gridApi, setGridApi] = useState<GridApi>()
  const [columnApi, setColumnApi] = useState<ColumnApi>()
  const [savedFilterModel, setSavedFilterModel] = useState<{ [k: string]: any }>()
  const [savedColumnState, setSavedColumnState] = useState<ColumnState[]>()
  const admin = useSelector((state: StoreState) => state.admin)
  const canGenerateNotices = hasCapability(admin, capabilities.CAPABILITY_TYPES.PAY_OR_QUIT_SENDER)

  const firstDayOfMonth = new Date(year, month - 1, 1).toISOString()
  const lastDayOfMonth = new Date(year, month, 0).toISOString()

  // 1st query to fetch most collection-related data
  const [{ data: collections, fetching: fetchingCollections }, refetchData] =
    useCollectionsByMonthQuery({
      variables: { firstDayOfMonth, lastDayOfMonth },
      requestPolicy: 'network-only',
      context: React.useMemo(() => ({ suspense: false }), []) // Disable suspense for this query too
    })

  // 2nd query to fetch remaining amount unpaid for each collection - takes a long time to run
  // Because of this, we never fetch this data besides on initial page load
  const [{ data: liabilitiesRemaining }] = useLiabilitiesRemainingForCollectionsQuery({
    variables: { firstDayOfMonth, lastDayOfMonth },
    requestPolicy: 'network-only',
    context: React.useMemo(() => ({ suspense: false }), [])
  })

  // Clear API references when month/year changes (which causes Grid to be recreated)
  useEffect(() => {
    setGridApi(undefined)
    setColumnApi(undefined)
  }, [month, year])

  // Join the data from the two queries together
  const liabilitiesRemainingDict = liabilitiesRemaining
    ? Object.fromEntries(liabilitiesRemaining.collections.map((lr) => [lr.id, lr]))
    : undefined
  const collectionRows = collections
    ? collections.collections
        .filter((c) => {
          // Filter out test rental in production
          if (process.env.NODE_ENV === 'production') {
            return c.rental.id !== process.env.REACT_APP_TEST_RENTAL_ID
          }
          return true
        })
        .map((c) => ({
          collection: c,
          liabilitiesRemaining: liabilitiesRemainingDict
            ? liabilitiesRemainingDict[c.id]
            : undefined
        }))
    : undefined

  const onGridReady = (evt: GridReadyEvent) => {
    setGridApi(evt.api)
    setColumnApi(evt.columnApi)

    // If available, reset user's saved filters & column state
    if (savedFilterModel || savedColumnState) {
      evt.api.setFilterModel(savedFilterModel)
      evt.columnApi.applyColumnState({ state: savedColumnState })
    } else {
      // Default column state set upon initial page load
      evt.columnApi.applyColumnState({
        state: [
          {
            colId: 'address',
            sort: 'asc'
          }
        ]
      })
    }
  }

  const handleCsvExport = () => {
    const currentTimestamp = new Date().getTime()
    gridApi?.exportDataAsCsv({
      allColumns: true,
      fileName: `collections_export_${currentTimestamp}.csv`
    })
  }

  const saveStateAndRefetch = () => {
    // Before refetching data (which destroys the grid)
    // save the user's current filters & column state
    setSavedFilterModel(gridApi?.getFilterModel())
    setSavedColumnState(columnApi?.getColumnState())
    refetchData({ requestPolicy: 'network-only' })
  }

  // Only show loading state if collections query is fetching
  const isLoading = fetchingCollections || !collectionRows

  return (
    <>
      <Helmet>
        <title>Collections Manager</title>
      </Helmet>
      <Stack padding={4} spacing={4}>
        <HStack justifyContent='space-between' flexWrap='wrap' rowGap={2}>
          <Heading>Collections Manager</Heading>
          <HStack flexWrap='wrap' justifyContent='flex-end'>
            {canGenerateNotices && (
              <Button
                as={RouterLink}
                to={payOrQuitPostingHref({ state: period.state, month, year })}
                size='sm'
                variant='outline'
                leftIcon={<PrinterIcon className='h-4 w-4' />}
              >
                Generate notices for posting
              </Button>
            )}
            <Button
              size='xs'
              onClick={handleCsvExport}
              leftIcon={<ArrowDownTrayIcon className='h-4 w-4' />}
              colorScheme={'teal'}
            >
              Export to CSV
            </Button>
          </HStack>
        </HStack>
        <HStack>
          <Stack flex={1}>
            <Heading size='md'>Month</Heading>
            <Select value={month} onChange={(e) => setMonth(Number(e.currentTarget.value))}>
              {months.map((m, idx) => (
                <option key={m} value={idx + 1}>
                  {m}
                </option>
              ))}
            </Select>
          </Stack>
          <Stack flex={1}>
            <Heading size='md'>Year</Heading>
            <Select value={year} onChange={(e) => setYear(Number(e.currentTarget.value))}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </Stack>
        </HStack>
        {isLoading ? (
          <Spinner />
        ) : (
          <Grid
            data={collectionRows}
            refetchData={saveStateAndRefetch}
            onGridReady={onGridReady}
            // fetchingLiabilities={fetchingLiabilitiesRemaining}
          />
        )}
      </Stack>
    </>
  )
}

export default Collections
